import { useContext, useEffect, useMemo, useRef, useState } from "react"
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import _ from "underscore";
import { ToastContext } from "../../../context/ToastContext";
import { dateHoverRange } from "../../../utils/Helper";

export const useTimesheetSummary = ({ filterValues, setFilterValues, tab, getData }) => {
    const { showInfo } = useContext(ToastContext);
    const [loader, setLoader] = useState(false);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [issueType] = useState([]);
    const [repCount, setRepCount] = useState([]);
    const tempRef = useRef([]);
    const gridApi = useRef(null);
    const coldef = [
        {
            field: "manager",
            headerName: "Manager Name",
            width: 150,
            pinned: 'left',
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellRenderer: function (params) {
                return (
                    <div>{params.value !== undefined ? params.value : "-"}</div>
                )
            },
        },
        {
            field: "member_name",
            headerName: "Member Name",
            width: 150,
            pinned: 'left',
            wrapHeaderText: true,
            autoHeaderHeight: true,
        },
        {
            field: "working_hours",
            headerName: "Working Hours",
            width: 80,
            headerClass: "hilighted-col",
            pinned: 'left',
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellRenderer: function (params) {
                return (
                    <div className="hilighted-col" style={{ textAlign: "right" }}>{params.value !== undefined ? params.value : "-"}</div>
                )
            }
        },
        {
            field: "pending_hours",
            headerName: "Pending Hours",
            width: 80,
            headerClass: "hilighted-col",
            pinned: 'left',
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellRenderer: function (params) {
                return (
                    <div className="hilighted-col" style={{ textAlign: "right" }}>{params.value !== undefined ? params.value : "-"}</div>
                )
            }
        },
        {
            field: "total",
            headerName: "Total Logged Hours",
            width: 80,
            pinned: 'left',
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellRenderer: function (params) {
                return (
                    <div className="hilighted-col" style={{ textAlign: "right" }}>{params.value !== undefined ? params.value : "-"}</div>
                )
            }
        },
    ];
    const [columnDefs, setColumnDefs] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [tableHeaderData, setTableHeaderData] = useState({});
    const [columns] = useState([
        { field: 'day_date', header: 'Date' },
        { field: 'issue_key', header: 'Issue key' },
        { field: 'issue_summary', header: 'Issue Summary' },
        { field: 'comment', header: 'Comment' },
        { field: 'timeSpent', header: 'Time Spent' },
    ]);

    useEffect(() => {
        gridApi?.current?.refreshCells();
    }, [repCount, rowData])

    // call api on filter change
    useEffect(() => {
        if (filterValues?.date?.[0] !== undefined && filterValues?.date?.[1] !== undefined && tab === "summary" && filterValues?.apiCall) {
            getManagerTimesheetSummary();
            setFilterValues((prev) => ({ ...prev, apiCall: false, isChange: false }));
        } else if (filterValues?.date?.[0] === undefined && filterValues?.date?.[1] === undefined && tab?.length !== 0) {
            showInfo("info", "Please select date");
        }
        // eslint-disable-next-line
    }, [filterValues?.apiCall, filterValues?.date])

    useEffect(() => {
        calculateTotal();
        // eslint-disable-next-line
    }, [rowData, columnDefs])

    useEffect(() => {
        if (!loader) {
            setColumnDefs(_.sortBy(columnDefs, "order_number"));
        }
        // eslint-disable-next-line
    }, [loader])

    // get manager timesheet summary data
    const getManagerTimesheetSummary = async () => {
        setLoader(true);
        try {
            const start = (typeof filterValues?.date?.[0] === "string" ? filterValues?.date?.[0] : dateHoverRange(filterValues?.date?.[0])) || "";
            const end = (typeof filterValues?.date?.[1] === "string" ? filterValues?.date?.[1] : dateHoverRange(filterValues?.date?.[1])) || "";
            const payload = {
                from_date: start || "",
                to_date: end || "",
                projects: _.pluck(filterValues?.projects, "project_id")?.toString() || "",
                users: _.pluck(filterValues?.users, "user_code")?.toString() || "",
                issueTypes: _.pluck(filterValues?.issue_type, "issue_type_id").toString() || "",
                project_lead_user_code: _.pluck(filterValues?.project_leads, "user_code").toString() || "",
            };

            const res = await services.getInvokeRemote("POST", Urls("GETMANAGERTIMESHEETSUMMARY"), payload);
            if (!res) {
                setLoader(false);
                return;
            }

            const data = res?.body?.data?.manager_Timesheet_Summary || [];
            const columns = res?.body?.data?.issue_types_data || [];
            const grouped = {};
            columns.forEach(({ issue_type_group_name, issue_type_id, issue_type_name, order_number }) => {
                const group = issue_type_group_name || "Others";
                const field = issue_type_id.toString();

                if (!grouped[group]) grouped[group] = [];
                if (!columnDefs.some(col => col.field === field)) {
                    grouped[group].push({
                        field,
                        headerName: issue_type_name,
                        order_number,
                        width: 75,
                        wrapHeaderText: true,
                        autoHeaderHeight: true,
                        headerClass: "ag-text-center",
                        cellRenderer: ({ value }) => (
                            <div style={{ textAlign: "center", cursor: "pointer" }}>
                                {value !== undefined ? value : "-"}
                            </div>
                        ),
                    });
                }
            });

            const groupedCols = Object.entries(grouped).map(([groupName, children]) => ({
                headerName: groupName,
                marryChildren: true,
                children,
            }));
            const cols = [...coldef]
            groupedCols.forEach((val) => {
                if (!_.findWhere(cols, { headerName: val.headerName })) {
                    cols?.push(val)
                }
            })
            setColumnDefs(cols);
            const issueTypeGroupMap = {};
            columns.forEach(col => {
                issueTypeGroupMap[col.issue_type_id.toString()] = col.issue_type_group_name || "";
            });

            const groupedMap = new Map();
            data.forEach(item => {
                const userId = item.user_code;
                const issueTypeId = item.issue_type_id.toString();
                const loggedHours = parseFloat(item.logged_hours) || 0;

                if (!groupedMap.has(userId)) {
                    const uniqueGroups = Array.from(new Set(Object.values(issueTypeGroupMap)));
                    const issueGroupsInit = uniqueGroups.reduce((acc, group) => {
                        acc[group] = [];
                        return acc;
                    }, {});

                    groupedMap.set(userId, {
                        member_user_code: userId,
                        member_name: item.user_display_name,
                        manager: item.manager_name,
                        working_hours: item.working_hours,
                        pending_hours: item?.pending_hours,
                        total: item?.logged_hours_total ? parseFloat(item?.logged_hours_total)?.toFixed(2) : 0,
                        issue_type_groups: issueGroupsInit,
                    });
                }

                const userEntry = groupedMap.get(userId);
                userEntry[issueTypeId] = item.logged_hours;

                const groupName = issueTypeGroupMap[issueTypeId];
                userEntry.issue_type_groups[groupName]?.push({
                    issue_type_id: issueTypeId,
                    issue_type_name: item.issue_type_name,
                    hours: item.logged_hours,
                });
            });

            const rowData = Array.from(groupedMap.values()).map(user => {
                return user;
            });

            const sortedRowData = _.sortBy(rowData, ['manager', 'member_name']);

            const repCount = _.chain(sortedRowData)
                .groupBy('manager')
                .map((users, manager) => ({ manager, count: users.length }))
                .value();

            setRepCount(repCount);
            tempRef.current = repCount;
            setRowData(sortedRowData);
            getData(sortedRowData);

            setLoader(false);
        } catch (error) {
            console.error(error);
            setLoader(false);
        }
    };


    // set total row at bottom
    const calculateTotal = () => {
        let obj = { member_name: "Total" };
        let cols = [];
        columnDefs?.forEach((col) => {
            if (col.field !== "member_name" && col.field !== "manager") {
                cols.push(col.field);
            }
        })

        cols?.forEach((item) => {
            const data = _.pluck(rowData, item);
            let sum = 0
            data?.forEach((val) => {
                if (val !== undefined && !_.isNaN(parseFloat(val))) {
                    sum += parseFloat(val);
                }
            })
            obj[item] = sum.toFixed(2);
        })

        setPinnedBottomRowData([obj]);

    }

    // get timesheet logs details
    const onCellClicked = async (event) => {
        if (event.value === undefined) {
            setShowPopup(false);
            return;
        }
        if (event.colDef.field !== "member_name" && event.colDef.field !== "manager" && event.colDef.field !== "working_hours" && event.colDef.field !== "pending_hours" && event.colDef.field !== "total") {
            try {
                const start = (typeof filterValues?.date?.[0] === "string" ? filterValues?.date?.[0] : dateHoverRange(filterValues?.date?.[0])) || "";
                const end = (typeof filterValues?.date?.[1] === "string" ? filterValues?.date?.[1] : dateHoverRange(filterValues?.date?.[1])) || "";
                const payload = {
                    "timesheet_user_code": event.data.member_user_code,
                    "from_date": start || "",
                    "to_date": end || "",
                    "issue_type_id": parseInt(event.colDef.field),
                    "projects": _.pluck(filterValues?.projects, "project_id")?.toString() || "",
                }
                const res = await services.getInvokeRemote("POST", Urls("GETMANAGERTIMESHEETDETAILS"), payload);

                if (res) {
                    const data = res?.body?.data?.manager_Timesheet_details;
                    setTableHeaderData({
                        member_name: event.data.member_name,
                        total_hours: event.value,
                        issue_type_name: data?.[0]?.issue_type_name
                    });
                    setTableData(_.sortBy(data, "day_date"));
                    setShowPopup(true);
                }

            } catch (error) {
                console.error(error);
            }
        }

    }

    const onGridReady = (params) => {
        gridApi.current = params.api;
    };

    return useMemo(() => ({
        columnDefs, onGridReady, rowData, onCellClicked, pinnedBottomRowData, showPopup, setShowPopup, tableHeaderData, tableData, columns, loader
    }), [columnDefs, onGridReady, rowData, onCellClicked, pinnedBottomRowData, showPopup, setShowPopup, tableHeaderData, tableData, columns, loader])
}